import React, {FC} from 'react'

// Interfaces for SKU sync response
interface SkuConnection {
  storeName: string;
  productTitle: string;
  variantName: string;
  sku: string;
  shopifyProductId: string;
  shopifyVariantId: string;
}

interface SkuSyncSummary {
  storesProcessed: string[];
  totalNewConnections: number;
  connectionsMade: SkuConnection[];
}

interface SkuSyncResponse {
  message: string;
  summary: SkuSyncSummary;
}

interface KangerSkuSyncModalProps {
  show: boolean;
  onClose: () => void;
  syncData?: SkuSyncResponse;
  loading: boolean;
  syncLogs: string[];
  error?: string;
}

export const KangerSkuSyncModal: FC<KangerSkuSyncModalProps> = ({
  show,
  onClose,
  syncData,
  loading,
  syncLogs,
  error
}) => {
  if (!show) return null;

  return (
    <div className='modal show d-block' tabIndex={-1} style={{backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999}}>
      <div className='modal-dialog modal-xl modal-dialog-scrollable'>
        <div className='modal-content'>
          <div className='modal-header border-0 pb-0'>
            <h4 className='modal-title fw-bold text-info'>
              <i className='fas fa-link me-2'></i>
              SKU Sync Results
            </h4>
            <button type='button' className='btn-close' onClick={onClose}></button>
          </div>
          
          <div className='modal-body px-4'>
            {loading ? (
              <div className='text-center py-5'>
                <div className='spinner-border text-info mb-3' style={{width: '3rem', height: '3rem'}} role='status'>
                  <span className='visually-hidden'>Loading...</span>
                </div>
                <h5 className='text-muted'>Syncing SKUs...</h5>
                <p className='text-muted mb-2'>
                  Please wait while we match local products to Shopify products via SKU.
                  You can see the live progress below.
                </p>
                <div 
                  className='card-body text-white p-3 rounded mt-3 text-start' 
                  style={{maxHeight: '400px', overflowY: 'auto', fontFamily: 'monospace', fontSize: '0.8rem'}}
                >
                  {syncLogs.map((log, i) => <div key={i}>{log}</div>)}
                </div>
              </div>
            ) : error ? (
                <div className='alert alert-danger'>
                    <i className='fas fa-exclamation-triangle me-2'></i>
                    <strong>Sync Failed:</strong> {error}
                </div>
            ) : syncData ? (
              <>
                {/* Overall Summary Stats */}
                <div className='row mb-4'>
                  <div className='col-12'>
                    <div className='card border-0 shadow-sm'>
                      <div className='card-body p-4'>
                        <h5 className='card-title text-dark mb-3'>
                          <i className='fas fa-chart-pie me-2 text-info'></i>
                          Sync Summary
                        </h5>
                        <div className='row g-3'>
                            <div className='col-md-6'>
                                <div className='d-flex align-items-center'>
                                    <div className='flex-shrink-0'>
                                        <div className='bg-info rounded-circle d-flex align-items-center justify-content-center' style={{width: '40px', height: '40px'}}>
                                            <i className='fas fa-store text-white'></i>
                                        </div>
                                    </div>
                                    <div className='flex-grow-1 ms-3'>
                                        <div className='fw-bold fs-4 text-info'>{syncData.summary.storesProcessed.join(', ')}</div>
                                        <div className='text-muted small'>Stores Processed</div>
                                    </div>
                                </div>
                            </div>
                            <div className='col-md-6'>
                                <div className='d-flex align-items-center'>
                                    <div className='flex-shrink-0'>
                                        <div className='bg-success rounded-circle d-flex align-items-center justify-content-center' style={{width: '40px', height: '40px'}}>
                                            <i className='fas fa-link text-white'></i>
                                        </div>
                                    </div>
                                    <div className='flex-grow-1 ms-3'>
                                        <div className='fw-bold fs-4 text-success'>{syncData.summary.totalNewConnections}</div>
                                        <div className='text-muted small'>New Connections Made</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='mt-3'>
                          <div className='alert alert-success mb-0'>
                            <i className='fas fa-check-circle me-2'></i>
                            {syncData.message}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* New Connections Table */}
                {syncData.summary.connectionsMade.length > 0 ? (
                  <div className='card border-0 shadow-sm'>
                    <div className='card-header bg-white border-0 pb-0'>
                        <h5 className='mb-0 fw-bold text-dark'>
                            <i className='fas fa-list-ul me-2 text-success'></i>
                            Newly Connected Products ({syncData.summary.totalNewConnections})
                        </h5>
                    </div>
                    <div className='card-body'>
                        <div className='table-responsive'>
                            <table className='table table-hover table-sm'>
                                <thead className='table-light'>
                                    <tr>
                                        <th className='fw-semibold'>Store</th>
                                        <th className='fw-semibold'>Product</th>
                                        <th className='fw-semibold'>Variant</th>
                                        <th className='fw-semibold'>SKU</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {syncData.summary.connectionsMade.map((conn, index) => (
                                    <tr key={index}>
                                        <td><span className='badge bg-info'>{conn.storeName}</span></td>
                                        <td className='fw-medium'>{conn.productTitle}</td>
                                        <td><span className='badge bg-light text-dark'>{conn.variantName}</span></td>
                                        <td><span className='badge bg-secondary'>{conn.sku}</span></td>
                                    </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                  </div>
                ) : (
                    <div className='text-center py-4'>
                        <i className='fas fa-info-circle text-muted me-2'></i>
                        <span className='text-muted'>No new connections were made during this sync.</span>
                    </div>
                )}
              </>
            ) : (
              <div className='text-center py-5'>
                <i className='fas fa-exclamation-circle text-muted mb-3' style={{fontSize: '3rem'}}></i>
                <h5 className='text-muted'>No sync data available</h5>
              </div>
            )}
          </div>
          
          <div className='modal-footer border-0 pt-0'>
            <button type='button' className='btn btn-secondary' onClick={onClose}>
              <i className='fas fa-times me-2'></i>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}; 